import { Banner } from "@/components/flexkit/Banner";
import { Button } from "@/components/flexkit/Button";
import {
    DialogBody,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Text } from "@/components/podkit/typography/Text";
import { useRegenerateSCIMToken, type PlainSCIMConfiguration } from "@/queries/scim-queries";
import { formatError } from "@/utils/errors";
import { useCallback, type FC } from "react";

type RegenerateSCIMTokenModalProps = {
    config: PlainSCIMConfiguration;
    onClose: () => void;
    onTokenRegenerated: (token: string) => void;
};

export const RegenerateSCIMTokenModal: FC<RegenerateSCIMTokenModalProps> = ({ config, onClose, onTokenRegenerated }) => {
    const { toast } = useToast();
    const regenerateMutation = useRegenerateSCIMToken();

    const handleRegenerate = useCallback(async () => {
        try {
            const result = await regenerateMutation.mutateAsync(config.id);
            toast({
                title: "Token regenerated successfully",
                description: "The previous token has been invalidated.",
            });
            onTokenRegenerated(result.token);
        } catch (error) {
            toast({
                title: "Failed to regenerate token",
                description: formatError(error),
            });
        }
    }, [config.id, onTokenRegenerated, regenerateMutation, toast]);

    return (
        <>
            <DialogHeader>
                <DialogTitle>Regenerate SCIM token?</DialogTitle>
                <DialogDescription>This will invalidate your existing token.</DialogDescription>
            </DialogHeader>

            <DialogBody className="flex flex-col gap-4">
                <Banner
                    variant="warning"
                    text="The current token will stop working immediately after regeneration."
                />

                <Text className="text-base">
                    Any identity provider using the current token will stop working until you update it with the new token.
                </Text>

                <Text className="text-base text-content-secondary">
                    Make sure you have access to your identity provider&apos;s SCIM configuration so you can update the token
                    right away.
                </Text>
            </DialogBody>

            <DialogFooter>
                <Button variant="outline" onClick={onClose} data-tracking-id="cancel-regenerate-scim-token">
                    Cancel
                </Button>
                <Button
                    variant="destructive"
                    onClick={handleRegenerate}
                    loading={regenerateMutation.isPending}
                    disabled={regenerateMutation.isPending}
                    data-tracking-id="confirm-regenerate-scim-token"
                >
                    Regenerate token
                </Button>
            </DialogFooter>
        </>
    );
};
